import Immutable from 'immutable';
import store from 'store2';
import { push } from 'react-router-redux';

// Retry helper for API calls
const withRetry = async (fn, maxRetries = 3, delayMs = 1000) => {
  let lastError;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      // Don't retry on authentication errors (401, 403) or validation errors (400)
      if (error.code && [400, 401, 403].includes(error.code)) {
        throw error;
      }
      if (attempt < maxRetries) {
        // Exponential backoff: 1s, 2s, 4s...
        await new Promise(resolve => setTimeout(resolve, delayMs * Math.pow(2, attempt - 1)));
      }
    }
  }
  throw lastError;
};

const LOAD = 'auth/LOAD';
const LOAD_SUCCESS = 'auth/LOAD_SUCCESS';
const LOAD_FAIL = 'auth/LOAD_FAIL';

const LOGIN = 'auth/LOGIN';
const LOGIN_SUCCESS = 'auth/LOGIN_SUCCESS';
const LOGIN_FAIL = 'auth/LOGIN_FAIL';

const DENTIST_LIST_SUCCESS = 'auth/DENTIST_LIST_SUCCESS';
const DENTIST_LIST_FAIL = 'auth/DENTIST_LIST_FAIL';

const SIGNUP = 'auth/SIGNUP';
const SIGNUP_SUCCESS = 'auth/SIGNUP_SUCCESS';
const SIGNUP_FAIL = 'auth/SIGNUP_FAIL';
const SET_ACTIVE_PROFILE = 'auth/SET_ACTIVE_PROFILE';
const CLOSE_ERROR_MESSAGE = 'auth/CLOSE_ERROR_MESSAGE';

const LOGOUT = 'auth/LOGOUT';

const FLUSH = 'auth/FLUSH';

const VERIFY = 'auth/VERIFY';
const VERIFY_SUCCESS = 'auth/VERIFY_SUCCESS';
const VERIFY_FAIL = 'auth/VERIFY_FAIL';

const SET_MYCDP_URI = 'auth/SET_MYCDP_URI';
const initialState = Immutable.fromJS({
  isLoad: false,
  loadErr: null,
  isLogin: false,
  loginErr: null,
  user: null,
  loading: false,
  signupError: null,
  activeProfile: null,
  dentistList: null,
  mycdp_uri: "",
  isVerifying: false,
  isVerified: false,
  verifyErr: null,
});

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case LOAD:
      return state
        .set('isLoad', true)
        .set('loadErr', null);

    case LOAD_SUCCESS:
      return state
        .set('isLoad', false)
        .set('user', action.user);

    case LOAD_FAIL:
      return state
        .set('isLoad', false)
        .set('loadErr', action.error)
        .set('user', {});


    case LOGIN:
      return state
        .set('isLogin', true)
        .set('loginErr', null);

    case LOGIN_SUCCESS:
      return state
        .set('isLogin', false)
        .set('user', action.user);

    case LOGIN_FAIL:
      return state
        .set('isLogin', false)
        .set('loginErr', action.error)
        .set('user', null);

    case DENTIST_LIST_SUCCESS:
      return state
        .set('loading', false)
        .set('dentistList', action.result);

    case DENTIST_LIST_FAIL:
      return state
        .set('loading', false)
        .set('dentistList', null);

    case SIGNUP:
      return state
        .set('loading', true)
        .set('signupError', null);

    case SIGNUP_SUCCESS:
      return state
        .set('loading', false)
        .set('signupError', null)
        .set('user', action.user);

    case SIGNUP_FAIL:
      return state
        .set('loading', false)
        .set('signupError', action.error)
        .set('user', null);

    case VERIFY:
      return state
        .set('isVerifying', true)
        .set('verifyErr', null);

    case VERIFY_SUCCESS:
      return state
        .set('isVerifying', false)
        .set('isVerified', true);

    case VERIFY_FAIL:
      return state
        .set('isVerifying', false)
        .set('isVerified', false)
        .set('verifyErr', action.error);

    case LOGOUT:
    case FLUSH: {
      return initialState;
    }
    case SET_ACTIVE_PROFILE:
      return state
        .set('activeProfile', action.result);
    case SET_MYCDP_URI:
      return state
        .set('mycdp_uri', action.mycdp_uri);
    case CLOSE_ERROR_MESSAGE:
      return state
        .set('loginErr', null);
    default:
      return state;
  }
}

export const hideErrorModal = () => async (dispatch, getState, api) => {
  dispatch({ type: CLOSE_ERROR_MESSAGE, action: null });
}

export function getUserHomeUrl(permission, defaultPage) {
  let homeUrl = '';
  if (defaultPage) {
    homeUrl = defaultPage;
  } else {
    homeUrl = 'schedule';
  }
  return homeUrl;
}

export function redirectUser(user) {
  if ((user && user.UdrUsersPermissions && user.UdrUsersPermissions.length === 0) || !user.UdaDrList.isActive) {
    return 'not-authorize';
  }

  if (user && user.UdrUsersPermissions) {
    for (var i = 0; i < user.UdrUsersPermissions.length; i++) {
      return getUserHomeUrl(user.UdrUsersPermissions[i].roleid, user.UdaDrList.defaultPage);
    }
  }
}

export function setActiveProfile(profile) {
  return (dispatch, getState) => new Promise(function (resolve, reject) {
    dispatch({ type: SET_ACTIVE_PROFILE, result: profile });
  });
}

const updateUserForAbbr = (user, abbr) => {
  const doctorDetails = user.drList?.[2]?.[abbr] ?? user.drList?.[3]?.[abbr];
  if (doctorDetails) {
    user.UdaDrList.userId = doctorDetails.userId;
    user.UdaDrList.firstStart = doctorDetails.firstStart;
    user.UdaDrList.prodAdjWo = doctorDetails.prodAdjWo ?? 100;
  }
};

export const changeDoctorAbbr = (abbr) => async (dispatch, getState) => {
  const user = getState().get('auth').get('user');
  if (!abbr || !user) return false;

  if (abbr === 'undo') {
    user.UdaDrList.abbr = user.UdaDrList.defaultAbbr;
    user.UdaDrList.prodAdjWo = user.UdaDrList.defaultProdAdjWo;
    user.UdaDrList.userId = user.UdaDrList.defaultUserId;
    user.UdaDrList.firstStart = user.UdaDrList.defaultFirstStart;
  } else {
    user.UdaDrList.abbr = abbr;
    updateUserForAbbr(user, abbr);
  }

  dispatch({ type: LOAD_SUCCESS, user });
  return true;
}

export const load = (forced) => async (dispatch, getState, api) => {
  // dont call api if user data is in state
  const user = getState().get('auth').get('user');
  if (user && !forced) {
    return;
  }
  // Also skip the full profile fetch if a persisted user exists and this is a passive check.
  // forced=true is only used after an actual login, where a fresh fetch is required.
  if (!forced && store.get('authUser')) {
    return;
  }
  dispatch({ type: LOAD });
  try {
    const res = await withRetry(() => api.get('/sessions'));
    if (res.message) {
      dispatch({ type: LOAD_FAIL, error: res.message });
      return;
    }
    if (res.accessToken) {
      store('authToken', res.accessToken);
    }
    store('authUser', res);
    dispatch({ type: LOAD_SUCCESS, user: res });
    return res;
  } catch (error) {
    dispatch({ type: LOAD_FAIL, error: error });
    return false;
  }
};

export const verifyToken = () => async (dispatch, getState, api) => {
  // If user is already in Redux state this session (e.g., just logged in), skip
  const currentUser = getState().get('auth').get('user');
  if (currentUser) return;

  // Rehydrate immediately from persisted storage so the UI is not blank
  const persistedUser = store.get('authUser');
  if (persistedUser) {
    dispatch({ type: LOAD_SUCCESS, user: persistedUser });
  }

  // No token means nothing to verify — clear any stale persisted data
  const token = store('authToken');
  if (!token) {
    if (persistedUser) {
      store.remove('authUser');
      dispatch({ type: LOGOUT });
    }
    return;
  }

  dispatch({ type: VERIFY });
  try {
    await api.get('/sessions/verify');
    dispatch({ type: VERIFY_SUCCESS });
  } catch (error) {
    dispatch({ type: VERIFY_FAIL, error });
    // Token invalid or expired — clear everything and send user to login
    store.remove('authToken');
    store.remove('authUser');
    store.remove('loginWithForm');
    dispatch({ type: LOGOUT });
  }
};

export const login = (profileObj, redirectURL = '') => async (dispatch, getState, api) => {
  store.remove('loginWithForm');
  dispatch({ type: LOGIN });
  try {
    let postObj = {};
    let url = '/sessions?jwt=1';
    if (profileObj.loginWithForm) {
      postObj = profileObj;
      url = '/login/sessions?jwt=1'
    } else {
      // Server verifies the Google ID token and takes the email from it
      postObj = { credential: profileObj.credential };
    }
    const res = await withRetry(() => api.post(url, { data: postObj }));
    dispatch({ type: LOGIN_SUCCESS, user: res });
    store('authToken', res.accessToken);
    store('loginWithForm', (res.user.loginWithForm));
    const homeUrl = redirectURL ?? `/${redirectUser(res.user)}`;
    dispatch(setActiveProfile());
    dispatch(load(true));
    dispatch(push(homeUrl));
    // Don't block login on savePartners - fire and forget
    api.post('/savePartners', { data: { userId: res.user.id, partnerName: "Dentist" } }).catch(() => { });
    return res;
  } catch (err) {
    dispatch({ type: LOGIN_FAIL, error: err.message });
  }
};

export const logout = () => (dispatch, getState, api) => {
  store.remove('authToken');
  store.remove('refreshToken');
  store.remove('loginWithForm');
  store.remove('authUser');
  localStorage.removeItem('reviewNotificationsModalShown');
  localStorage.removeItem('myReviewedChartsNotificationModalShown');
  dispatch({ type: LOGOUT });
  dispatch({ type: 'FLUSH' });
  dispatch(push('/'));
};

export const getDentistList = () => async (dispatch, getState, api) => {
  try {
    const res = await api.get('/getDentistList');
    dispatch({ type: DENTIST_LIST_SUCCESS, result: res });
    // return res;
  } catch (err) {
    dispatch({ type: DENTIST_LIST_FAIL, error: err.message });
  }
};
export const cdpLogin = (accessToken, redirectURL) => async (dispatch, getState, api) => {
  try {
    const res = await withRetry(() => api.get(`/sessions/cdpLogin/${accessToken}`));
    if (res) {
      dispatch({ type: LOGIN_SUCCESS, user: res });
      store('authToken', res.accessToken);
      store('loginWithForm', (res.user.loginWithForm));
      const homeUrl = redirectUser(res.user);
      dispatch(setActiveProfile());
      dispatch(load(true));
      if (redirectURL) {
        dispatch(push(redirectURL));
      } else {
        dispatch(push(`/${homeUrl}`));
      }
      return res;
    }
  } catch (error) {
    return error;
  }
};
export const getDentalPartners = (id) => async (dispatch, getState, api) => {
  try {
    const res = await api.get(`/getPartnersList/${id}`, {});
    dispatch({ type: SET_MYCDP_URI, mycdp_uri: res.partners_uri });
    return res;
  } catch (err) {
    return err;
  }
}
